import type { BigIntish, MarketId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { vaultV2Abi } from '@morpho-org/blue-sdk-viem'
import { UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils'
import { oneYearInSecs } from '@repo/utils'
import { formatUnits, getAbiItem, toFunctionSelector } from 'viem'

import { sortPendingTimelocks as sortV1PendingTimelocks } from '@/components/VaultPage/VaultV1/VaultPendingTimelock/utils'
import { TIMELOCKED_FUNCTIONS } from '@/lib/orpc/schemas'
import {
  VaultTimelockedFunctionName,
  VaultV2CapType,
  VaultV2TimelockedFunctionName
} from '@/types/enums'

import type {
  UnderlyingVaultInfo,
  UnderlyingVaultPendingTimelock,
  UnderlyingVaultPendingTimelockWithCaps,
  VaultV2Caps,
  VaultV2PendingTimelock,
  VaultV2PendingTimelockDecodedData,
  VaultV2Timelocks
} from '../types/pendingTimelocks'
import type { VaultV2State } from '../VaultV2Hero/state.contract'
import type { AdapterVaultAction, CapTarget, PendingAction } from './pendingActions.contract'
import type { Timelock } from './timelocks.contract'

import { sortPendingTimelocks } from '../VaultV2PendingTimelock/utils'

export interface PendingTimelocksInput {
  chainId: number
  items: readonly PendingAction[]
  adapterVaultActions: readonly AdapterVaultAction[]
  /** The vault's asset, read only when an absolute cap is pending. */
  asset: VaultV2State['asset'] | null
  /** The vault's timelocks, read only when a timelock change is pending. */
  timelocks: readonly Timelock[]
}

export interface PendingTimelocks {
  pendingTimelocks: VaultV2PendingTimelock[]
  underlyingVaultItems: UnderlyingVaultPendingTimelockWithCaps[]
  caps: VaultV2Caps
  /** Plain data: the client parses it into a token, which can't cross the server boundary. */
  vaultAsset: Asset | null
  timelocks: VaultV2Timelocks[]
  currentPerformanceFee: number | undefined
  currentManagementFee: number | undefined
  currentPerformanceFeeRecipient: Address | undefined
  currentManagementFeeRecipient: Address | undefined
}

type DecodedData<TTypename extends NonNullable<VaultV2PendingTimelockDecodedData>['__typename']> =
  Extract<NonNullable<VaultV2PendingTimelockDecodedData>, { __typename: TTypename }>
type CapData = DecodedData<'VaultV2IncreaseCapPendingData'>['config']['data']
type CapDataOf<TTypename extends NonNullable<CapData>['__typename']> = Extract<
  NonNullable<CapData>,
  { __typename: TTypename }
>
type Adapter = NonNullable<CapDataOf<'AdapterCapData'>['adapter']>
export type Asset = NonNullable<CapDataOf<'CollateralCapData'>['collateralToken']>
type TokenLabel = NonNullable<AdapterVaultAction['market']['loanToken']>
type V1Market = NonNullable<
  Extract<
    NonNullable<UnderlyingVaultPendingTimelock['decodedData']>,
    { __typename: 'VaultSetCapPendingData' }
  >['market']
>

const functionNameBySelector = new Map<string, string>(
  TIMELOCKED_FUNCTIONS.map(name => [
    toFunctionSelector(getAbiItem({ abi: vaultV2Abi, name })),
    name
  ])
)

function toMarketId(marketId: `0x${string}`): MarketId {
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return marketId as MarketId
}

/**
 * WAD fees as the yearly fraction the fee rows read, like the API's `performanceFee` and
 * `managementFee`. The management fee accrues per second on-chain.
 */
function toFeeFraction(type: 'setPerformanceFee' | 'setManagementFee', fee: string): number {
  const fraction = Number(formatUnits(BigInt(fee), 18))
  return type === 'setManagementFee' ? fraction * oneYearInSecs : fraction
}

export function toAsset(
  chainId: number,
  token: TokenLabel,
  priceUsd: number | null = null
): Asset | null {
  if (token.symbol === null || token.decimals === null) return null
  return {
    id: `${chainId}-${token.address}`,
    address: token.address,
    symbol: token.symbol,
    name: token.symbol,
    decimals: token.decimals,
    price: priceUsd === null ? null : { usd: priceUsd },
    tags: null,
    logoURI: token.logoUri,
    chain: { id: chainId }
  }
}

/** A collateral asset, with placeholder symbol and decimals when Core cannot label the token. */
export function toCollateralAsset(chainId: number, token: TokenLabel): Asset | null {
  return toAsset(chainId, {
    ...token,
    symbol: token.symbol ?? UNKNOWN_SYMBOL,
    decimals: token.decimals ?? 18
  })
}

/**
 * The adapter the hero links to. Only the V1 vault's name, address and logo render; the asset's
 * symbol and decimals are the V2 vault's own, which every adapter shares.
 */
function toAdapter(
  chainId: number,
  adapter: Address,
  identity: Pick<Extract<PendingAction, { type: 'addAdapter' }>, 'adapterKind' | 'adapterVault'>,
  asset: Asset | null
): Adapter | null {
  const adapterAsset = {
    id: `${chainId}-${asset?.address ?? adapter}`,
    logoURI: identity.adapterVault?.assetLogoUri ?? asset?.logoURI ?? null,
    symbol: asset?.symbol ?? '',
    decimals: asset?.decimals ?? 18
  }
  if (identity.adapterKind === 'vaultV1' && identity.adapterVault) {
    return {
      __typename: 'MetaMorphoAdapter',
      id: adapter,
      address: adapter,
      metaMorpho: {
        name: identity.adapterVault.name ?? '',
        address: identity.adapterVault.address,
        asset: adapterAsset,
        chain: { id: chainId }
      }
    }
  }
  if (identity.adapterKind === 'marketV1') {
    return {
      __typename: 'MorphoMarketV1Adapter',
      id: adapter,
      address: adapter,
      vault: { id: adapter, asset: adapterAsset }
    }
  }
  return null
}

/** A V1 row names its market and formats its cap in the market's loan token. */
export function hasV1LoanTokens(actions: readonly AdapterVaultAction[]): boolean {
  return actions.every(
    ({ market }) => market.loanToken?.symbol != null && market.loanToken.decimals != null
  )
}

function toV1Market(
  chainId: number,
  market: AdapterVaultAction['market'],
  priceUsd: number | null
) {
  const loanAsset = market.loanToken && toAsset(chainId, market.loanToken, priceUsd)
  if (!loanAsset) return null
  const v1Market: V1Market = {
    chain: { id: chainId },
    marketId: toMarketId(market.marketId),
    loanAsset,
    collateralAsset: market.collateralToken && toCollateralAsset(chainId, market.collateralToken)
  }
  return v1Market
}

function toCapConfig(
  chainId: number,
  target: CapTarget,
  asset: Asset | null
): { type: VaultV2CapType; data: CapData } {
  switch (target.kind) {
    case 'adapter':
      return {
        type: VaultV2CapType.Adapter,
        data: {
          __typename: 'AdapterCapData',
          adapterAddress: target.adapter,
          adapter: toAdapter(chainId, target.adapter, target, asset)
        }
      }
    case 'collateral':
      return {
        type: VaultV2CapType.Collateral,
        data: {
          __typename: 'CollateralCapData',
          collateralAddress: target.token.address,
          collateralToken: toCollateralAsset(chainId, target.token)
        }
      }
    case 'market': {
      const loanAsset = toAsset(chainId, target.loanToken)
      return {
        type: VaultV2CapType.MarketV1,
        data: {
          __typename: 'MarketV1CapData',
          adapterAddress: target.adapter,
          adapter: {
            __typename: 'MorphoMarketV1Adapter',
            id: target.adapter,
            address: target.adapter
          },
          market: loanAsset && {
            chain: { id: chainId },
            marketId: toMarketId(target.marketId),
            loanAsset,
            collateralAsset:
              target.collateralToken && toCollateralAsset(chainId, target.collateralToken),
            lltv: target.lltv
          }
        }
      }
    }
    case 'unknown':
      return { type: VaultV2CapType.Unknown, data: null }
  }
}

function toDecodedData(
  chainId: number,
  action: PendingAction,
  asset: Asset | null,
  capId: `0x${string}`
): Pick<VaultV2PendingTimelock, 'functionName' | 'decodedData'> {
  switch (action.type) {
    case 'setIsAllocator':
      return {
        functionName: VaultV2TimelockedFunctionName.SetIsAllocator,
        decodedData: {
          __typename: 'VaultV2SetIsAllocatorPendingData',
          account: { address: action.account },
          isAllocator: action.isAllocator
        }
      }
    case 'setReceiveSharesGate':
      return {
        functionName: VaultV2TimelockedFunctionName.SetReceiveSharesGate,
        decodedData: {
          __typename: 'VaultV2SetReceiveSharesGatePendingData',
          receiveSharesGate: action.gate
        }
      }
    case 'setSendSharesGate':
      return {
        functionName: VaultV2TimelockedFunctionName.SetSendSharesGate,
        decodedData: {
          __typename: 'VaultV2SetSendSharesGatePendingData',
          sendSharesGate: action.gate
        }
      }
    case 'setReceiveAssetsGate':
      return {
        functionName: VaultV2TimelockedFunctionName.SetReceiveAssetsGate,
        decodedData: {
          __typename: 'VaultV2SetReceiveAssetsGatePendingData',
          receiveAssetsGate: action.gate
        }
      }
    case 'setSendAssetsGate':
      return {
        functionName: VaultV2TimelockedFunctionName.SetSendAssetsGate,
        decodedData: {
          __typename: 'VaultV2SetSendAssetsGatePendingData',
          sendAssetsGate: action.gate
        }
      }
    case 'setAdapterRegistry':
      return {
        functionName: VaultV2TimelockedFunctionName.SetAdapterRegistry,
        decodedData: {
          __typename: 'VaultV2SetAdapterRegistryPendingData',
          adapterRegistry: action.adapterRegistry
        }
      }
    case 'addAdapter':
    case 'removeAdapter':
      return {
        functionName:
          action.type === 'addAdapter'
            ? VaultV2TimelockedFunctionName.AddAdapter
            : VaultV2TimelockedFunctionName.RemoveAdapter,
        decodedData: {
          __typename: 'VaultV2AdapterPendingData',
          adapterAddress: action.adapter,
          adapter: toAdapter(chainId, action.adapter, action, asset)
        }
      }
    case 'increaseTimelock':
    case 'decreaseTimelock':
      return {
        functionName:
          action.type === 'increaseTimelock'
            ? VaultV2TimelockedFunctionName.IncreaseTimelock
            : VaultV2TimelockedFunctionName.DecreaseTimelock,
        decodedData: {
          __typename: 'VaultV2TimelockPendingData',
          functionName:
            functionNameBySelector.get(action.selector.toLowerCase()) ?? action.selector,
          selector: action.selector,
          timelock: action.newDuration
        }
      }
    case 'abdicate':
      return {
        functionName: VaultV2TimelockedFunctionName.Abdicate,
        decodedData: {
          __typename: 'VaultV2AbdicatePendingData',
          functionName:
            functionNameBySelector.get(action.selector.toLowerCase()) ?? action.selector,
          selector: action.selector
        }
      }
    case 'setPerformanceFee':
      return {
        functionName: VaultV2TimelockedFunctionName.SetPerformanceFee,
        decodedData: {
          __typename: 'VaultV2SetPerformanceFeePendingData',
          performanceFee: action.fee
        }
      }
    case 'setManagementFee':
      return {
        functionName: VaultV2TimelockedFunctionName.SetManagementFee,
        decodedData: { __typename: 'VaultV2SetManagementFeePendingData', managementFee: action.fee }
      }
    case 'setPerformanceFeeRecipient':
      return {
        functionName: VaultV2TimelockedFunctionName.SetPerformanceFeeRecipient,
        decodedData: {
          __typename: 'VaultV2SetPerformanceFeeRecipientPendingData',
          performanceFeeRecipient: action.recipient
        }
      }
    case 'setManagementFeeRecipient':
      return {
        functionName: VaultV2TimelockedFunctionName.SetManagementFeeRecipient,
        decodedData: {
          __typename: 'VaultV2SetManagementFeeRecipientPendingData',
          managementFeeRecipient: action.recipient
        }
      }
    case 'increaseAbsoluteCap':
    case 'increaseRelativeCap': {
      const { type, data } = toCapConfig(chainId, action.target, asset)
      return {
        functionName:
          action.type === 'increaseAbsoluteCap'
            ? VaultV2TimelockedFunctionName.IncreaseAbsoluteCap
            : VaultV2TimelockedFunctionName.IncreaseRelativeCap,
        decodedData: {
          __typename: 'VaultV2IncreaseCapPendingData',
          cap: action.type === 'increaseAbsoluteCap' ? action.absoluteCap : action.relativeCap,
          config: { id: capId, idData: '0x', type, data }
        }
      }
    }
    case 'setForceDeallocatePenalty':
      return {
        functionName: VaultV2TimelockedFunctionName.SetForceDeallocatePenalty,
        decodedData: {
          __typename: 'VaultV2SetForceDeallocatePenaltyPendingData',
          adapterAddress: action.adapter,
          forceDeallocatePenalty: action.penalty
        }
      }
  }
}

function toUnderlyingVaultItem(
  chainId: number,
  action: AdapterVaultAction
): UnderlyingVaultPendingTimelockWithCaps {
  const vault: UnderlyingVaultInfo = {
    name: action.vault.name ?? '',
    address: action.vault.address,
    assetLogoURI: action.vault.assetLogoUri
  }
  const common = { validAt: action.executableAt, txHash: action.txHash }

  if (action.type === 'removeMarket') {
    return {
      vault,
      marketSupplyCaps: {},
      pendingTimelock: {
        ...common,
        functionName: VaultTimelockedFunctionName.RemoveMarket,
        decodedData: {
          __typename: 'VaultRemoveMarketPendingData',
          market: toV1Market(chainId, action.market, null)
        }
      }
    }
  }

  const marketSupplyCaps: Record<string, BigIntish> =
    action.currentSupplyCap === null ? {} : { [action.market.marketId]: action.currentSupplyCap }
  return {
    vault,
    marketSupplyCaps,
    pendingTimelock: {
      ...common,
      functionName: VaultTimelockedFunctionName.SetCap,
      decodedData: {
        __typename: 'VaultSetCapPendingData',
        supplyCap: action.supplyCap,
        market: toV1Market(chainId, action.market, action.assetPriceUsd)
      }
    }
  }
}

/**
 * The procedures' outputs in the shape the pending timelock rows render. Each cap increase gets
 * its own cap id, so its current value sits next to it in `caps` only when Core serves one.
 */
export function toPendingTimelocks({
  chainId,
  items,
  adapterVaultActions,
  asset,
  timelocks
}: PendingTimelocksInput): PendingTimelocks {
  const vaultAsset = asset && toAsset(chainId, { ...asset, logoUri: null }, asset.priceUsd)
  const caps: VaultV2Caps = []
  const pendingTimelocks = items.map((action, index): VaultV2PendingTimelock => {
    const capId = `0x${index.toString(16)}` as const
    const { functionName, decodedData } = toDecodedData(chainId, action, vaultAsset, capId)

    if (action.type === 'increaseAbsoluteCap' && action.currentAbsoluteCap !== null) {
      caps.push({ id: capId, absoluteCap: action.currentAbsoluteCap, relativeCap: '0' })
    }
    if (action.type === 'increaseRelativeCap' && action.currentRelativeCap !== null) {
      caps.push({ id: capId, absoluteCap: '0', relativeCap: action.currentRelativeCap })
    }

    return { validAt: action.executableAt, txHash: action.txHash, functionName, decodedData }
  })

  const currentFee = (type: 'setPerformanceFee' | 'setManagementFee') => {
    const fee = items.find(
      (action): action is Extract<PendingAction, { type: typeof type }> =>
        action.type === type && action.currentFee !== null
    )?.currentFee
    return fee == null ? undefined : toFeeFraction(type, fee)
  }
  const currentRecipient = (type: 'setPerformanceFeeRecipient' | 'setManagementFeeRecipient') =>
    items.find(
      (action): action is Extract<PendingAction, { type: typeof type }> =>
        action.type === type && action.currentRecipient !== null
    )?.currentRecipient ?? undefined

  const underlyingItems = new Map(
    adapterVaultActions.map(action => {
      const item = toUnderlyingVaultItem(chainId, action)
      return [item.pendingTimelock, item] as const
    })
  )
  const underlyingVaultItems = sortV1PendingTimelocks([...underlyingItems.keys()]).flatMap(
    pendingTimelock => underlyingItems.get(pendingTimelock) ?? []
  )

  return {
    pendingTimelocks: sortPendingTimelocks(pendingTimelocks),
    underlyingVaultItems,
    caps,
    vaultAsset,
    timelocks: timelocks.map(({ functionName, duration }) => ({ functionName, duration })),
    currentPerformanceFee: currentFee('setPerformanceFee'),
    currentManagementFee: currentFee('setManagementFee'),
    currentPerformanceFeeRecipient: currentRecipient('setPerformanceFeeRecipient'),
    currentManagementFeeRecipient: currentRecipient('setManagementFeeRecipient')
  }
}
